/**
 * Horarios › Turnos (008 · T044 · US1; contracts/ui.md §1.3, FR-001–FR-003).
 *
 * Tabla de plantillas (Código, Nombre, Horario, Duración, Refrigerio, Tolerancias, Origen, Estado)
 * con la versión vigente hoy, filtro de estado en borrador con "Buscar" y "Actualizar" (QA-2), y el
 * primario "Nuevo turno" solo con `schedules.configure` (el encargado de sede no lo ve). "Ver" abre
 * el drawer con las versiones y las acciones. Empresa suspendida ⇒ acciones deshabilitadas.
 */
import { Clock, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CatalogStatusFilterSchema, type CatalogStatusFilter } from '@fermin/contracts';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../../components/ui/card';
import { EmptyState } from '../../../components/ui/empty-state';
import { Field } from '../../../components/ui/field';
import { FilterActions, FilterForm } from '../../../components/ui/filter-actions';
import { RefreshButton } from '../../../components/ui/refresh-button';
import { Select, type SelectOption } from '../../../components/ui/select';
import { SkeletonLines } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../../components/ui/table';
import { t } from '../../../i18n';
import { useAppliedFilters } from '../../../lib/use-applied-filters';
import {
  ManagedChip,
  ManagedSourceLabel,
  useIntegrationSystemName,
} from '../../integration/managed-by';
import { hasPermission, useMe } from '../../shell/use-me';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { useShiftTemplates } from '../api';
import { durationLabel, timeRange, toleranceLabel } from '../format';

import { TemplateDrawer } from './template-drawer';
import { TemplateFormDialog } from './template-form-dialog';

interface TemplateFilters {
  status: CatalogStatusFilter;
}

const DEFAULT_FILTERS: TemplateFilters = { status: 'ACTIVE' };

export function TemplatesTab() {
  const me = useMe();
  const systemName = useIntegrationSystemName();
  const { readOnly } = useReadOnly();
  const canConfigure = hasPermission(me.data, 'schedules.configure');
  const settings = me.data?.company?.settings;
  // Sin `company.settings.write` (encargado) la API no manda `settings`: tolerancias desconocidas.
  const companyLate = settings?.late_tolerance_minutes ?? null;
  const companyEarly = settings?.early_leave_tolerance_minutes ?? null;
  const companyTimezone = me.data?.company?.default_timezone ?? 'America/Lima';

  const { draft, applied, update, apply, clear, dirty, isFiltered, canClear } =
    useAppliedFilters<TemplateFilters>(DEFAULT_FILTERS);
  const query = useShiftTemplates(applied.status);
  const templates = query.data ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const statusOptions: SelectOption[] = useMemo(
    () =>
      CatalogStatusFilterSchema.options.map((status) => ({
        value: status,
        label: t(`schedules.templates.filters.status.${status}`),
      })),
    [],
  );

  return (
    <div className="flex flex-col gap-fm-4">
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        <p className="text-fm-sm text-fm-text-secondary">{t('schedules.templates.description')}</p>
        <div className="flex flex-wrap items-center gap-fm-2">
          <RefreshButton query={query} />
          {canConfigure ? (
            <Button
              variant="compact"
              size="md"
              icon={<Plus size={20} />}
              data-testid="shift-template-new"
              onClick={() => setCreateOpen(true)}
              {...readOnlyProps(readOnly)}
            >
              {t('schedules.templates.new')}
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <FilterForm onApply={apply} className="block">
          <fieldset className="flex flex-wrap items-end gap-fm-3">
            <legend className="sr-only">{t('schedules.templates.filters.legend')}</legend>
            <Field label={t('schedules.templates.filters.statusLabel')} className="w-[200px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.status}
                  onValueChange={(value) =>
                    update({ status: CatalogStatusFilterSchema.parse(value) })
                  }
                  options={statusOptions}
                />
              )}
            </Field>
            <FilterActions onSearch={apply} dirty={dirty} canClear={canClear} onClear={clear} />
          </fieldset>
        </FilterForm>
      </Card>

      {query.isPending ? <SkeletonLines lines={4} /> : null}

      {query.isError ? (
        <Card>
          <CardTitle>{t('schedules.templates.error.title')}</CardTitle>
          <CardDescription>{t('schedules.templates.error.description')}</CardDescription>
          <Button className="mt-fm-4" size="sm" onClick={() => void query.refetch()}>
            {t('common.buttons.retry')}
          </Button>
        </Card>
      ) : null}

      {query.isSuccess && templates.length === 0 ? (
        <EmptyState
          icon={Clock}
          title={
            isFiltered
              ? t('schedules.templates.noResults.title')
              : t('schedules.templates.empty.title')
          }
          description={
            isFiltered
              ? t('schedules.templates.noResults.description')
              : t('schedules.templates.empty.description')
          }
        />
      ) : null}

      {templates.length > 0 ? (
        <Table data-testid="shift-templates-table">
          <TableCaption>{t('schedules.tabs.turnos')}</TableCaption>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('schedules.templates.columns.code')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.templates.columns.name')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.templates.columns.schedule')}</TableHeaderCell>
              <TableHeaderCell numeric>{t('schedules.templates.columns.duration')}</TableHeaderCell>
              <TableHeaderCell numeric>{t('schedules.templates.columns.break')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.templates.columns.tolerances')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.templates.columns.source')}</TableHeaderCell>
              <TableHeaderCell>{t('schedules.templates.columns.status')}</TableHeaderCell>
              <TableHeaderCell>
                <span className="sr-only">{t('schedules.templates.columns.detail')}</span>
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow
                key={template.id}
                data-testid="shift-template-row"
                selected={selected === template.id}
              >
                <TableCell className="font-semibold">{template.code}</TableCell>
                <TableCell>
                  <span className="inline-flex flex-wrap items-center gap-fm-2">
                    {template.name}
                    {template.source === 'INTEGRATION' ? (
                      <ManagedChip systemName={systemName} />
                    ) : null}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex flex-col">
                    <span className="fm-tabular">
                      {timeRange(template.current.start_time, template.current.end_time)}
                    </span>
                    {template.crosses_midnight ? (
                      <span className="text-fm-xs text-fm-text-secondary">
                        {t('schedules.templates.crossesMidnight')}
                      </span>
                    ) : null}
                  </span>
                </TableCell>
                <TableCell numeric>{durationLabel(template.duration_minutes)}</TableCell>
                <TableCell numeric>
                  {t('schedules.format.minutes', { minutes: template.current.break_minutes })}
                </TableCell>
                <TableCell>{toleranceLabel(template.current, companyLate, companyEarly)}</TableCell>
                <TableCell>
                  <ManagedSourceLabel source={template.source} systemName={systemName} />
                </TableCell>
                <TableCell>
                  {template.status === 'ACTIVE' ? (
                    <Badge variant="activa">{t('schedules.templates.status.ACTIVE')}</Badge>
                  ) : (
                    <Badge variant="inactiva">{t('schedules.templates.status.ARCHIVED')}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('schedules.templates.viewLabel', { name: template.name })}
                    onClick={() => setSelected(template.id)}
                  >
                    {t('schedules.templates.columns.detail')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      <TemplateDrawer
        templateId={selected}
        onClose={() => setSelected(null)}
        canConfigure={canConfigure}
        companyTimezone={companyTimezone}
        companyLateTolerance={companyLate}
        companyEarlyTolerance={companyEarly}
      />

      {canConfigure ? (
        <TemplateFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          companyLateTolerance={companyLate}
          companyEarlyTolerance={companyEarly}
        />
      ) : null}
    </div>
  );
}
